import React, { useEffect, useState } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import ChakraSidebar from './components/ChakraSidebar';
import { MasterDashboard, DiretoriaDashboard, AssociadoDashboard } from './components/Dashboard';
import DashboardPlanejador from './components/Dashboard/DashboardPlanejador';
import DashboardFinancas from './components/Dashboard/DashboardFinancas';
import ContasReceber from './components/Financeiro/ContasReceber/ContasReceber';
import ContasPagar from './components/Financeiro/ContasPagar/ContasPagar.js';
import Configuracoes from './components/Configuracoes/Configuracoes';
import  ClientesPage  from './components/Clientes/ClientesPage.js';
import EmailPage from './components/Email';
import { TipoClientePage } from './components/TipoCliente';
import { TermosDeUsoAdmin } from './components/TermosDeUso';
import { PoliticaPrivacidadeAdmin } from './components/PoliticaPrivacidade';
import { FAQAdmin } from './components/FAQ';
import PlannerPage from './components/Planner';
import AgendaPage from './components/Agenda';
import { clearAllAuthData } from '../../utils/authUtils';
import './PainelPage.css';

const PainelPage = () => {
  const navigate = useNavigate();
  
  // State to track both actual and displayed user type
  const [actualUserType, setActualUserType] = useState('');
  const [displayedUserType, setDisplayedUserType] = useState('');
  
  // On initial load, set up the user types
  useEffect(() => {  
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'user') {
      clearAllAuthData();
      navigate('/login');
      return;
    }
    
    // Get the actual user type from localStorage
    const storedUserType = localStorage.getItem('userType');
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    
    // If userType is missing but we have userData, try to detect it from permissions
    if (!storedUserType && userData && userData.permissions) {
      let detectedType = 'associado'; // Default
      
      if (userData.permissions.includes('Master')) {
        detectedType = 'master';
      } else if (userData.permissions.includes('Diretoria')) {
        detectedType = 'diretoria';
      } else if (userData.permissions.includes('Gestor')) {
        detectedType = 'gestor';
      }
      
      localStorage.setItem('userType', detectedType);
      setActualUserType(detectedType);
      setDisplayedUserType(detectedType);
      return;
    }
    
    if (storedUserType) {
      setActualUserType(storedUserType);
      
      // By default, display the actual user type
      setDisplayedUserType(storedUserType);
      
      // Only master users can simulate other types
      if (storedUserType === 'master') {
        const simulatedType = localStorage.getItem('simulatedUserType');
        
        if (simulatedType) {
          setDisplayedUserType(simulatedType);
        }
      } else {
        // For non-master users, make sure we don't have any simulations stored
        localStorage.removeItem('simulatedUserType');
      }
    } else {
      // No user type found, try to infer it from userData if available
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      
      if (userData && userData.permissions) {
        let userType = 'associado'; // Default
        
        if (userData.permissions.includes('Master')) {
          userType = 'master';
        } else if (userData.permissions.includes('Diretoria')) {
          userType = 'diretoria';
        } else if (userData.permissions.includes('Gestor')) {
          userType = 'gestor';
        }
        
        localStorage.setItem('userType', userType);
        setActualUserType(userType);
        setDisplayedUserType(userType);
        return;
      }
      
      // No user type found and can't infer, this shouldn't happen
      console.error('No user type found in localStorage and cannot infer from user data!');
      clearAllAuthData(false); // Don't preserve any data
      navigate('/login');
    }
  }, [navigate]);
  
  // Handle changing the simulated user type
  const handleUserTypeChange = (e) => {
    const newType = e.target.value;
    
    // Update the displayed type
    setDisplayedUserType(newType);
    
    // Store the simulation setting
    localStorage.setItem('simulatedUserType', newType);
  };
  
  // Handle resetting to the actual user type
  const handleResetUserType = () => {
    setDisplayedUserType(actualUserType);
    localStorage.removeItem('simulatedUserType');
  };
  
  // Log out function with thorough cleanup
  const handleLogout = () => {    
    // Use our utility function to clear all auth data
    clearAllAuthData();
    
    // Navigate to login page
    navigate('/login');
  };
  
  // Render the appropriate dashboard based on displayed user type
  const renderDashboard = () => {
    switch (displayedUserType) {
      case 'master':
        return <MasterDashboard />;
      case 'diretoria':
        return <DiretoriaDashboard />;
      case 'gestor':
        return <AssociadoDashboard />; // For now, Gestores see the same dashboard as Associados
      case 'associado':
      default:
        return <AssociadoDashboard />;
    }
  };
  
  return (
    <ChakraProvider>
      <div className="painel-layout">
        <ChakraSidebar 
          userRole={displayedUserType} 
          onLogout={handleLogout}
        />
        
        <div className="painel-content">
          {/* Only show user type selector for actual master users */}
          {actualUserType === 'master' && (
            <div className="user-type-selector">
              <div className="selector-content">
                <label>Simular tipo de usuário: </label>
                <select value={displayedUserType} onChange={handleUserTypeChange}>
                  <option value="associado">Associado</option>
                  <option value="diretoria">Diretoria</option>
                  <option value="gestor">Gestor</option>
                  <option value="master">Master</option>
                </select>
                
                {displayedUserType !== actualUserType && (
                  <button 
                    className="reset-button" 
                    onClick={handleResetUserType}
                    title="Voltar para conta Master"
                  >
                    Resetar Simulação
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Main content area with nested routes */}
          <Routes>
            <Route path="/" element={renderDashboard()} />
            <Route path="/dashboard-planejador" element={<DashboardPlanejador />} />
            <Route path="/dashboard-financas" element={<DashboardFinancas />} />
            <Route path="/contas-receber" element={<ContasReceber />} />
            <Route path="/contas-pagar" element={<ContasPagar />} />
            <Route path="/email" element={<EmailPage />} />
            <Route path="/clientes" element={<ClientesPage />} />
            <Route path="/tipos-cliente" element={<TipoClientePage />} />
            <Route path="/planner" element={<PlannerPage />} />
            <Route path="/processos" element={<div className="section-placeholder">Área de Processos</div>} />
            <Route path="/agenda" element={<AgendaPage />} />
            <Route path="/relatorios" element={<div className="section-placeholder">Relatórios</div>} />
            <Route path="/termos-de-uso" element={<TermosDeUsoAdmin />} />
            <Route path="/politica-privacidade" element={<PoliticaPrivacidadeAdmin />} />
            <Route path="/faq" element={<FAQAdmin />} />
            <Route path="/configuracoes" element={<Configuracoes />} />
            <Route path="*" element={renderDashboard()} />
          </Routes>
        </div>
      </div>
    </ChakraProvider>
  );
};

export default PainelPage;